import { useCallback, useMemo } from 'react';

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDateOnly(value) {
  const raw = String(value == null ? '' : value).trim();
  if (!raw) return undefined;
  const match = raw.match(DATE_ONLY_RE);
  if (!match) return undefined;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, monthIndex, day, 0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function toDateOnly(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function startOfDay(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  );
}

function addDays(date, days) {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return startOfDay(next);
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

function formatDisplay(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  });
}

function diffDaysInclusive(from, to) {
  if (!(from instanceof Date) || !(to instanceof Date)) return 0;
  const msPerDay = 24 * 60 * 60 * 1000;
  const start = startOfDay(from).getTime();
  const end = startOfDay(to).getTime();
  return Math.round((end - start) / msPerDay) + 1;
}

function buildPresetRanges(today = new Date()) {
  const base = startOfDay(today);
  return [
    { id: 'today', label: 'Today', from: base, to: base },
    {
      id: 'yesterday',
      label: 'Yesterday',
      from: addDays(base, -1),
      to: addDays(base, -1)
    },
    { id: 'last7', label: 'Last 7 days', from: addDays(base, -6), to: base },
    { id: 'last30', label: 'Last 30 days', from: addDays(base, -29), to: base },
    { id: 'thisMonth', label: 'This month', from: startOfMonth(base), to: base }
  ];
}

export default function useDateRangeState({ fromDate, toDate, onChange }) {
  const selected = useMemo(() => {
    const from = parseDateOnly(fromDate);
    const to = parseDateOnly(toDate);
    return { from, to };
  }, [fromDate, toDate]);

  const setRange = useCallback(
    (range) => {
      const nextFrom = toDateOnly(range && range.from);
      const nextTo = toDateOnly(range && range.to);
      onChange({ fromDate: nextFrom, toDate: nextTo });
    },
    [onChange]
  );

  const defaultMonth = useMemo(() => {
    if (selected.from) return selected.from;
    if (selected.to) return selected.to;
    return new Date();
  }, [selected]);

  return {
    selected,
    setRange,
    defaultMonth
  };
}

export {
  parseDateOnly,
  toDateOnly,
  formatDisplay,
  diffDaysInclusive,
  buildPresetRanges,
  startOfDay
};
